import React from 'react'
import { Link, generatePath } from 'react-router-dom'
import { INotification } from 'types'
import { useProject } from 'hooks/useProject'
import Tooltip from 'components/tooltip'
import { Process, Spreadsheet } from 'components/icons'
import { DOCUMENT, TABLE } from 'routes'
import CountPopover from './CountPopover'

interface Props {
  notification: INotification
}

// The sidebar's icon for a document or table: 14px, a little heavier, and a little lighter than the name.
const ICON = 'flex shrink-0 opacity-70 [&>svg]:size-3.5 [&_path[stroke]]:[stroke-width:2.4]'
// As the audit log's resources: the body colour and medium weight (marked important over style.scss's link
// rules), underlined on hover.
const LINK = 'truncate font-medium! text-body hover:underline! underline-offset-2'

/** One document or table a webhook is limited to: its kind's icon and its name, linked to open it. */
const ResourceLink: React.FC<{ kind: 'document' | 'table'; name: string; to: string }> = ({ kind, name, to }) => (
  <li className="flex min-w-0 items-center gap-1.5">
    <span className={ICON} role="img" aria-label={kind === 'document' ? 'Document' : 'Table'}>
      {kind === 'document' ? <Process /> : <Spreadsheet />}
    </span>
    <Tooltip content={name} showOnlyIfTruncated delay={300}>
      <Link to={to} className={LINK}>
        {name}
      </Link>
    </Tooltip>
  </li>
)

/** "2 tables", "3 documents", or "3 resources" when there are both. */
const resourceCount = (documents: number, tables: number): string => {
  const count = documents + tables
  if (documents === 0) return `${count} ${count === 1 ? 'table' : 'tables'}`
  if (tables === 0) return `${count} ${count === 1 ? 'document' : 'documents'}`
  return `${count} resources`
}

/**
 * The documents and tables a webhook is limited to, or "All resources" when it isn't limited to any. One
 * shows as its icon and name, linked to open it; more show as a count ("2 tables") that opens a popover
 * listing them, each linked.
 */
const NotificationResourceList: React.FC<Props> = ({ notification }) => {
  const { project } = useProject()
  const { processes, tables } = notification

  if (processes.length === 0 && tables.length === 0) return <>All resources</>

  const links = [
    ...processes.map((process) => (
      <ResourceLink
        key={process.publicId}
        kind="document"
        name={process.name}
        to={generatePath(DOCUMENT, { projectId: project.publicId, processId: process.publicId })}
      />
    )),
    ...tables.map((table) => (
      <ResourceLink
        key={table.publicId}
        kind="table"
        name={table.name}
        to={generatePath(TABLE, {
          projectId: project.publicId,
          tableId: table.publicId,
          viewId: table.defaultViewId
        })}
      />
    ))
  ]

  if (links.length === 1) return <ul className="flex max-w-[220px] flex-col py-2">{links}</ul>

  return (
    <CountPopover
      count={resourceCount(processes.length, tables.length)}
      title="Resources"
      of={notification.description}
      id={`webhook-resources-${notification.publicId}`}
      rows={links.length}
    >
      {links}
    </CountPopover>
  )
}

export default NotificationResourceList
